import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Per-tenant statutory tax regime. Drives label swaps (GST↔VAT), sidebar
 * visibility for GST-only integrations (e-invoice, e-way bill), and the
 * customer tax-ID field. Mirrors the backend `Domain.Enums.TaxRegime` enum
 * — serialized as a string via `JsonStringEnumConverter`.
 */
export type TaxRegime = "None" | "Gst" | "Vat";

export interface UserInfo {
  id: string;
  email: string;
  fullName: string;
  tenantId: string;
  branchId?: string | null;
  roleName: string;
  permissions: string[];
  /** Tenant's tax regime — default `"Gst"` for back-compat with older login payloads. */
  taxRegime: TaxRegime;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: string | null;
  user: UserInfo | null;
  /** Runtime branch override (S7.5 multi-branch switcher). Falls back to user.branchId. */
  activeBranchId: string | null;
  setSession: (s: { accessToken: string; refreshToken: string; expiresAt: string; user: UserInfo }) => void;
  setActiveBranch: (branchId: string | null) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
  hasPermission: (p: string) => boolean;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      user: null,
      activeBranchId: null,
      setSession: ({ accessToken, refreshToken, expiresAt, user }) =>
        set({ accessToken, refreshToken, expiresAt, user, activeBranchId: user.branchId ?? null }),
      setActiveBranch: (branchId) => set({ activeBranchId: branchId }),
      clear: () => set({ accessToken: null, refreshToken: null, expiresAt: null, user: null, activeBranchId: null }),
      isAuthenticated: () => {
        const { accessToken, expiresAt } = get();
        if (!accessToken || !expiresAt) return false;
        return new Date(expiresAt).getTime() > Date.now();
      },
      hasPermission: (p) => get().user?.permissions.includes(p) ?? false,
    }),
    { name: "invmgmt-auth" }
  )
);
